import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://velko-pos.com';

const HREFLANG_LOCALES = [
  'fr',
  'fr-CI',
  'fr-SN',
  'fr-CM',
  'fr-BF',
  'fr-ML',
  'fr-GN',
  'fr-CD',
  'fr-CG',
] as const;

interface Breadcrumb {
  name: string;
  path: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  noindex?: boolean;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  breadcrumbs?: Breadcrumb[];
}

export default function SEOHead({
  title,
  description,
  path,
  ogImage = '/brand/og-default.png',
  noindex = false,
  jsonLd,
  breadcrumbs,
}: SEOHeadProps) {
  const canonicalUrl = `${BASE_URL}${path}`;
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`;

  const allJsonLd: Record<string, unknown>[] = jsonLd
    ? Array.isArray(jsonLd)
      ? [...jsonLd]
      : [jsonLd]
    : [];

  if (breadcrumbs && breadcrumbs.length > 0) {
    allJsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: crumb.name,
        item: `${BASE_URL}${crumb.path}`,
      })),
    });
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {HREFLANG_LOCALES.map((locale) => (
        <link key={locale} rel="alternate" hrefLang={locale} href={canonicalUrl} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:locale" content="fr_FR" />
      <meta property="og:site_name" content="Velko POS" />
      <meta property="og:locale:alternate" content="fr_CI" />
      <meta property="og:locale:alternate" content="fr_SN" />
      <meta property="og:locale:alternate" content="fr_CM" />
      <meta property="og:locale:alternate" content="fr_BF" />
      <meta property="og:locale:alternate" content="fr_ML" />
      <meta property="og:locale:alternate" content="fr_GN" />
      <meta property="og:locale:alternate" content="fr_CD" />
      <meta property="og:locale:alternate" content="fr_CG" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@VelkoPOS" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImageUrl} />

      {allJsonLd.map((item, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(item)}
        </script>
      ))}
    </Helmet>
  );
}
